import { LinearGradient } from 'expo-linear-gradient';
import { type ReactElement, useCallback, useState } from 'react';
import {
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  StyleSheet,
} from 'react-native';

// A ScrollView whose indicator is hidden tells the reader nothing: the list
// just stops mid-item and reads as "that is all there is". Three surfaces hit
// this — the Command Deck nav, the pipeline board and the settings nav — so the
// affordance lives here rather than being re-derived per surface.
//
// Usage: spread `scrollProps` onto the ScrollView, render <ScrollEdgeFade> for
// each end, and give the pair a `position: relative` wrapper that the fades can
// anchor to.

// Slack for sub-pixel offsets so a fade never flickers at either end of travel.
const EDGE_THRESHOLD = 4;
const FADE_SIZE = 32;

interface ScrollEdges {
  /** Content exists before the current offset (above, or to the left). */
  readonly atStart: boolean;
  /** Content exists past the current offset (below, or to the right). */
  readonly atEnd: boolean;
  readonly offset: number;
  readonly maxOffset: number;
  readonly scrollProps: {
    readonly onScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => void;
    readonly onLayout: (e: LayoutChangeEvent) => void;
    readonly onContentSizeChange: (w: number, h: number) => void;
    readonly scrollEventThrottle: number;
  };
}

/**
 * Tracks whether a ScrollView has content hidden before or after the visible
 * window. `enabled` short-circuits to false so a caller can switch the
 * affordance off (a stacked mobile layout, say) without breaking hook order.
 */
export function useScrollEdges(horizontal = false, enabled = true): ScrollEdges {
  const [offset, setOffset] = useState(0);
  const [viewport, setViewport] = useState(0);
  const [content, setContent] = useState(0);

  const onScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { x, y } = e.nativeEvent.contentOffset;
      setOffset(horizontal ? x : y);
    },
    [horizontal],
  );

  const onLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const { width, height } = e.nativeEvent.layout;
      setViewport(horizontal ? width : height);
    },
    [horizontal],
  );

  const onContentSizeChange = useCallback(
    (w: number, h: number) => {
      setContent(horizontal ? w : h);
    },
    [horizontal],
  );

  const scrollable = Math.max(0, content - viewport);

  return {
    atStart: enabled && offset > EDGE_THRESHOLD,
    atEnd: enabled && offset < scrollable - EDGE_THRESHOLD,
    offset,
    maxOffset: scrollable,
    scrollProps: { onScroll, onLayout, onContentSizeChange, scrollEventThrottle: 16 },
  };
}

interface ScrollEdgeFadeProps {
  /** Which end of the scroll axis this fade sits on. */
  readonly edge: 'top' | 'bottom' | 'left' | 'right';
  /** The surface behind the scroller — the fade resolves to this colour. */
  readonly color: string;
  /** Inset from the far edges, e.g. to clear a visible scrollbar. */
  readonly inset?: number;
  readonly size?: number;
}

export function ScrollEdgeFade({
  edge,
  color,
  inset = 0,
  size = FADE_SIZE,
}: ScrollEdgeFadeProps): ReactElement {
  const horizontal = edge === 'left' || edge === 'right';
  const leading = edge === 'top' || edge === 'left';

  return (
    <LinearGradient
      pointerEvents="none"
      colors={leading ? [color, 'transparent'] : ['transparent', color]}
      // Default gradient direction is vertical; only the horizontal edges need
      // the start/end override.
      {...(horizontal ? { start: { x: 0, y: 0.5 }, end: { x: 1, y: 0.5 } } : {})}
      style={[
        styles.fade,
        horizontal
          ? { width: size, top: 0, bottom: inset, [edge]: 0 }
          : { height: size, left: 0, right: inset, [edge]: 0 },
      ]}
    />
  );
}

const styles = StyleSheet.create({
  fade: {
    position: 'absolute',
  },
});
